import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const Api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  }),
  tagTypes: ['user', 'users'],
  endpoints: (builder) => ({
    getUsers: builder.query<any, void>({
      query: () => ({ url: '/users' }),
      providesTags: ['users'],
    }),
    getLeads: builder.query<any, void>({
      query: () => ({ url: '/demo/leads' }),
      transformResponse: (result: any) => result.leads,
    }),
    createLeads: builder.mutation<any, any>({
      query: (body) => ({ url: '/demo/leads', method: 'POST', body }),
    }),
    getUser: builder.query<any, string>({ query: (id) => ({ url: `/users/${id}` }) }),
    updateUser: builder.mutation<any, any>({
      query: ({ id, ...body }) => ({ url: `/users/${id}`, body, method: 'PUT' }),
    }),

    createUser: builder.mutation({
      query: (body) => ({ url: `/users`, method: 'POST', body }),
      invalidatesTags: (_, error) => (error ? [] : ['users']),
    }),
    login: builder.mutation({
      query: (body) => ({ url: `/users/login`, method: 'POST', body }),
      invalidatesTags: (_, error) => (error ? [] : ['user']),
    }),
    logout: builder.mutation<any, void>({
      query: () => ({ url: `/users/logout`, method: 'DELETE' }),
      invalidatesTags: (_, error) => (error ? [] : ['user']),
    }),
    me: builder.query<any, void>({ query: () => ({ url: `/users/me` }), providesTags: ['user'] }),
    createKYCLink: builder.mutation<any, any>({
      query: ({ id, ...body }) => ({ url: `/kyc`, method: 'POST', body }),
    }),
    uploadSelfie: builder.mutation<any, any>({
      query: ({ id, ...body }) => ({ url: `/kyc/${id}/selfie`, method: 'POST', body }),
    }),
    updateKyc: builder.mutation<any, any>({
      query: ({ id, ...body }) => ({ url: `/kyc/${id}`, method: 'PUT', body }),
    }),
    uploadVoiceAnswer: builder.mutation<any, { id: number; formData: FormData }>({
      query: ({ id, formData }) => ({ url: `/kyc/${id}/voice-answer`, method: 'POST', body: formData }),
    }),
    validateSelfie: builder.mutation<{ success: boolean; valid: boolean; reason: string | null }, { id: number; image: string }>({
      query: ({ id, image }) => ({
        url: `/kyc/${id}/validate-selfie`,
        method: 'POST',
        body: { image },
      }),
    }),
    kycList: builder.query<{ kycs: any[]; meta: any }, any>({
      query: (params) => ({ url: `/kyc`, params }),
    }),
    createTemplate: builder.mutation<any, any>({
      query: (body) => ({ url: `/templates`, method: 'POST', body }),
    }),
    template: builder.query<any, number>({
      query: (id) => ({ url: `/templates/${id}` }),
      transformResponse: (result: any) => result.template,
    }),
    templates: builder.query<any, void>({
      query: () => ({ url: `/templates` }),
      transformResponse: (result: any) => result.templates,
    }),
  }),
})

// Export hooks for usage in functional components
export const {
  useGetUserQuery,
  useGetUsersQuery,
  useUpdateUserMutation,
  useCreateUserMutation,
  useLoginMutation,
  useLogoutMutation,
  useMeQuery,
  useUploadSelfieMutation,
  useUpdateKycMutation,
  useUploadVoiceAnswerMutation,
  useValidateSelfieMutation,
  useKycListQuery,
  useCreateKYCLinkMutation,
  useGetLeadsQuery,
  useCreateLeadsMutation,
  useCreateTemplateMutation,
  useTemplateQuery,
  useTemplatesQuery,
  util: { getRunningQueriesThunk },
} = Api
